import React from 'react';
import { type ElementType, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';
import { match } from 'ts-pattern';
import type * as TF from 'type-fest';

import Box from '#/ui/components/box';
import type { Color } from '#/ui/types';
import type { IgnoreProp, OverridableStringUnion, OverrideProps } from '#/ui/utils/types';
import { cn } from '#/utils/classnames';

export type ButtonPropsVariantOverrides = TF.EmptyObject;

export type ButtonPropsColorOverrides = TF.EmptyObject;

export type ButtonPropsSizeOverrides = TF.EmptyObject;

export interface ButtonOwnProps {
  /**
   * The content of the component.
   */
  children?: React.ReactNode;
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: IgnoreProp;
  /**
   * The color of the component.
   * It supports both default and custom theme colors, which can be added as shown in the
   * @default 'primary'
   */
  color?: OverridableStringUnion<Color, ButtonPropsColorOverrides>;
  /**
   * If `true`, the component is disabled.
   * @default false
   */
  disabled?: boolean;
  /**
   * If `true`, no elevation is used.
   * @default false
   */
  disableElevation?: boolean;
  /**
   * If `true`, the  keyboard focus ripple is disabled.
   * @default false
   */
  disableFocusRipple?: boolean;
  /**
   * Element placed after the children.
   */
  endIcon?: React.ReactNode;
  /**
   * If `true`, the button will take up the full width of its container.
   * @default false
   */
  fullWidth?: boolean;
  /**
   * If `true`, the loading indicator is visible and the button is disabled.
   * @default false
   */
  loading?: boolean;
  /**
   * Element placed before the children if the button is in loading state.
   * The node should contain an element with `role="progressbar"` with an accessible name.
   * By default, it renders a `CircularProgress` that is labeled by the button itself.
   * @default <CircularProgress color="inherit" size={16} />
   */
  loadingIndicator?: React.ReactNode;
  /**
   * The loading indicator can be positioned on the start, end, or the center of the button.
   * @default 'center'
   */
  loadingPosition?: 'start' | 'end' | 'center';
  /**
   * The size of the component.
   * `small` is equivalent to the dense button styling.
   * @default 'medium'
   */
  size?: OverridableStringUnion<
    'xsmall' | 'small' | 'medium' | 'large' | 'xlarge',
    ButtonPropsSizeOverrides
  >;
  /**
   * Element placed before the children.
   */
  startIcon?: React.ReactNode;
  /**
   * The variant to use.
   * @default 'text'
   */
  variant?: OverridableStringUnion<'text' | 'outlined' | 'contained', ButtonPropsVariantOverrides>;
}

export type ButtonTypeMap<
  AdditionalProps = TF.EmptyObject,
  RootComponent extends React.ElementType = 'button',
> = {
  props: AdditionalProps & ButtonOwnProps;
  defaultComponent: RootComponent;
};

export type ButtonProps<
  RootComponent extends React.ElementType = ButtonTypeMap['defaultComponent'],
  AdditionalProps = TF.EmptyObject,
  // @ts-expect-error
> = OverrideProps<ButtonTypeMap<AdditionalProps, RootComponent>, RootComponent> & {
  component?: React.ElementType;
};

function Button<C extends ElementType = 'button'>(
  {
    component = 'button',
    children,
    variant = 'contained',
    color = 'primary',
    size = 'medium',
    disabled = false,
    fullWidth = false,
    startIcon,
    endIcon,
    loading = false,
    loadingIndicator,
    loadingPosition = 'start',
    className,
    ...props
  }: ButtonProps<C>,
  ref?: React.ForwardedRef<C>
) {
  const variantClassnames = useMemo(() => getVariantClassnames(variant, color), [variant, color]);

  const sizeClassnames = useMemo(() => getSizeClassnames(size), [size]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Allow
  const loadingElement = useMemo(() => {
    if (loadingIndicator) {
      return loadingIndicator;
    }
    return (
      <span className='mr-2'>
        <span
          className={cn(
            'd-loading d-loading-spinner',
            match(size)
              .with('xsmall', () => 'd-loading-xs')
              .otherwise(() => 'd-loading-sm')
          )}
        />
      </span>
    );
  }, [loadingIndicator]);

  return (
    <Box
      {...props}
      component={component}
      ref={ref}
      className={twMerge(
        'gooey-btn',
        'd-btn font-sans inline-flex items-center gap-0 justify-center [&_svg]:size-auto',
        startIcon && '[&_svg]:mr-1',
        endIcon && '[&_svg]:ml-2',
        variantClassnames,
        sizeClassnames,
        fullWidth && 'w-full',
        disabled && 'd-btn-disabled',
        className
      )}
      disabled={disabled || loading}
    >
      {match({ loadingPosition, startIcon, loading })
        .with({ loading: true, loadingPosition: 'start' }, () => (
          <React.Fragment>
            {loadingElement}
            <span className='mr-1' />
          </React.Fragment>
        ))
        .when(
          (o) => !!o.startIcon,
          () => (
            <React.Fragment>
              {startIcon}
              <span className='mr-1' />
            </React.Fragment>
          )
        )
        .otherwise(() => null)}
      {loading && loadingPosition === 'center' ? loadingElement : children}
      {match({ loadingPosition, endIcon, loading })
        .with({ loading: true, loadingPosition: 'end' }, () => (
          <React.Fragment>
            <span className='ml-2' />
            {loadingElement}
          </React.Fragment>
        ))
        .when(
          (o) => !!o.endIcon,
          () => (
            <React.Fragment>
              <span className='ml-2' />
              {endIcon}
            </React.Fragment>
          )
        )
        .otherwise(() => null)}
    </Box>
  );
}
Button.displayName = 'Button';

export default Button;

/*
|------------------
| Internals
|------------------
*/

const variantMap = {
  contained: {
    primary: 'd-btn-primary',
    secondary: 'd-btn-secondary',
    accent: 'd-btn-accent',
    error: 'd-btn-error',
    warning: 'd-btn-warning',
    info: 'd-btn-info',
    success: 'd-btn-success',
  },
  outlined: {
    primary: 'd-btn-outline d-btn-primary',
    secondary: 'd-btn-outline d-btn-secondary',
    accent: 'd-btn-outline d-btn-accent',
    error: 'd-btn-outline d-btn-error',
    warning: 'd-btn-outline d-btn-warning',
    info: 'd-btn-outline d-btn-info',
    success: 'd-btn-outline d-btn-success',
  },
  text: {
    primary: 'd-btn-ghost text-primary',
    secondary: 'd-btn-ghost text-secondary',
    accent: 'd-btn-ghost text-accent',
    error: 'd-btn-ghost text-error',
    warning: 'd-btn-ghost text-warning',
    info: 'd-btn-ghost text-info',
    success: 'd-btn-ghost text-success',
  },
} satisfies Record<
  Required<ButtonProps>['variant'],
  Record<Required<ButtonProps>['color'], string>
>;

const sizeMap = {
  xsmall: 'd-btn-xs',
  small: 'd-btn-sm',
  medium: '',
  large: 'd-btn-lg',
  xlarge: 'd-btn-xl',
} as const;

function getSizeClassnames(size: ButtonProps['size']) {
  return sizeMap[size ?? 'medium'];
}

function getVariantClassnames(variant: ButtonProps['variant'], color: ButtonProps['color']) {
  return variantMap[variant ?? 'contained'][color ?? 'primary'];
}
